/**
 * Composed Zustand store for MACDM — devtools → subscribeWithSelector → persist → immer.
 * Invariants: persist scope is whitelisted (sessionId/query/topology only); a vanilla
 * handle is exported so non-React consumers (SSE pipeline, tests) can read/write safely.
 */
import { create, type StoreApi, useStore } from "zustand";
import {
  devtools,
  persist,
  subscribeWithSelector,
  createJSONStorage,
} from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { createSessionSlice, type SessionSlice } from "./slices/sessionSlice";
import { createPersonaSlice, type PersonaSlice } from "./slices/personaSlice";
import { createStreamSlice, type StreamSlice } from "./slices/streamSlice";
import { createModeratorSlice, type ModeratorSlice } from "./slices/moderatorSlice";
import { createAudioSlice, type AudioSlice } from "./slices/audioSlice";
import { createVerdictSlice, type VerdictSlice } from "./slices/verdictSlice";
import { createWizardSlice, type WizardSlice } from "./slices/wizardSlice";
import {
  createInterjectionSlice,
  type InterjectionSlice,
} from "./slices/interjectionSlice";
import { createUiSlice, type UiSlice } from "./slices/uiSlice";
import { createVersionsSlice, type VersionsSlice } from "./slices/versionsSlice";
import { createBrainSlice, type BrainSlice } from "./slices/brainSlice";
import { createQualitySlice, type QualitySlice } from "./slices/qualitySlice";
import type { CritiqueStrictness, TerminationProfile } from "../domain/wizard";

export interface DebateStore
  extends SessionSlice,
    PersonaSlice,
    StreamSlice,
    ModeratorSlice,
    AudioSlice,
    VerdictSlice,
    WizardSlice,
    InterjectionSlice,
    UiSlice,
    VersionsSlice,
    BrainSlice,
    QualitySlice {}

export interface DebateStorePersistedShape {
  /**
   * `sessionId` is INTENTIONALLY NOT persisted. Carrying a finalized
   * debate's id across a page reload was the proximate cause of the
   * "stuck SONUÇLANDI badge" bug — the backend either replayed the
   * cached verdict the moment the SSE socket opened, or it 409'd on
   * row reuse, both of which manifested as "Tartışmayı Başlat dedim
   * bi bok olmadı". The new `pendingSessionId` slot below covers the
   * one legitimate cross-reload use case (RAG file staging).
   */
  pendingSessionId: string | null;
  query: string;
  topology: SessionSlice["topology"];
  wizardConfig?: {
    query: string;
    topology: SessionSlice["topology"];
    agentCount: number;
    maxRounds: number;
    autoFinalize: boolean;
    critiqueStrictness: CritiqueStrictness;
    /** Konsey Cognitive Renaissance — Bölüm A0/A1. */
    terminationProfile?: TerminationProfile;
    /**
     * Konsey Yönergesi. Soruyla birlikte kalıcı: reload sonrası soru geri
     * gelip yönerge sessizce kaybolursa operatör farkına varmadan
     * yönlendirmesiz bir tartışma başlatır.
     */
    userDebatePrompt?: string | null;
  };
}

const PERSIST_NAME = "macdm-session";
// Bumped from 1 → 2: dropped `sessionId` from the persisted shape and
// introduced `pendingSessionId` (RAG staging). Old persisted blobs would
// otherwise survive `merge()` and silently feed a stale `sessionId` back
// in via `current.sessionId`'s default — bumping the version forces a
// clean wipe on the first load after deploy.
const PERSIST_VERSION = 2;

const composedCreator = devtools(
  subscribeWithSelector(
    persist(
      immer<DebateStore>((...a) => ({
        ...createSessionSlice(...a),
        ...createPersonaSlice(...a),
        ...createStreamSlice(...a),
        ...createModeratorSlice(...a),
        ...createAudioSlice(...a),
        ...createVerdictSlice(...a),
        ...createWizardSlice(...a),
        ...createInterjectionSlice(...a),
        ...createUiSlice(...a),
        ...createVersionsSlice(...a),
        ...createBrainSlice(...a),
        ...createQualitySlice(...a),
      })),
      {
        name: PERSIST_NAME,
        version: PERSIST_VERSION,
        storage: createJSONStorage(() => {
          if (typeof window === "undefined") {
            // SSR / Node fallback — opt-out persistence quietly.
            const mem = new Map<string, string>();
            return {
              getItem: (k) => mem.get(k) ?? null,
              setItem: (k, v) => {
                mem.set(k, v);
              },
              removeItem: (k) => {
                mem.delete(k);
              },
            };
          }
          return window.localStorage;
        }),
        partialize: (state): DebateStorePersistedShape => ({
          // sessionId DELIBERATELY OMITTED — see DebateStorePersistedShape doc.
          pendingSessionId: state.pendingSessionId,
          query: state.query,
          topology: state.topology,
          wizardConfig: {
            query: state.wizard.query,
            topology: state.wizard.topology,
            agentCount: state.wizard.agentCount,
            maxRounds: state.wizard.maxRounds,
            autoFinalize: state.wizard.autoFinalize,
            critiqueStrictness: state.wizard.critiqueStrictness,
            terminationProfile: state.wizard.terminationProfile,
            userDebatePrompt: state.wizard.userDebatePrompt,
          },
        }),
        merge: (persisted, current) => {
          // Merge legacy persisted shape with the new wizard subtree.
          const p = (persisted ?? {}) as DebateStorePersistedShape;
          const merged = {
            ...current,
            // sessionId NEVER restored from disk. Always start `null` so
            // the next `start()` mints (or adopts pendingSessionId).
            sessionId: null,
            pendingSessionId: p.pendingSessionId ?? current.pendingSessionId,
            query: p.query ?? current.query,
            topology: p.topology ?? current.topology,
          };
          if (p.wizardConfig) {
            merged.wizard = {
              ...current.wizard,
              query: p.wizardConfig.query ?? current.wizard.query,
              topology: p.wizardConfig.topology ?? current.wizard.topology,
              agentCount: p.wizardConfig.agentCount ?? current.wizard.agentCount,
              maxRounds: p.wizardConfig.maxRounds ?? current.wizard.maxRounds,
              autoFinalize:
                typeof p.wizardConfig.autoFinalize === "boolean"
                  ? p.wizardConfig.autoFinalize
                  : current.wizard.autoFinalize,
              critiqueStrictness:
                p.wizardConfig.critiqueStrictness ?? current.wizard.critiqueStrictness,
              terminationProfile:
                p.wizardConfig.terminationProfile ?? current.wizard.terminationProfile,
              userDebatePrompt:
                p.wizardConfig.userDebatePrompt ?? current.wizard.userDebatePrompt,
            };
          }
          return merged;
        },
      },
    ),
  ),
  { name: "MACDM/DebateStore" },
);

export const useDebateStore = create<DebateStore>()(composedCreator);

/**
 * Vanilla store handle for use outside React (SSE pipeline, tests, workers).
 * Mirrors the React-bound store; the same composedCreator is reused so that
 * middleware ordering and persistence semantics stay identical.
 */
export const debateStore: StoreApi<DebateStore> =
  useDebateStore as unknown as StoreApi<DebateStore>;

/** Hook variant of the vanilla store for codepaths that hold a StoreApi reference. */
export function useFromVanilla<T>(selector: (s: DebateStore) => T): T {
  return useStore(debateStore, selector);
}
